import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { CalendarDays, Clock, MapPin } from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { SectionHeading } from "@/components/SectionHeading";
import { api } from "@/lib/api";
import { formatDateID, monthShortID, dayOf } from "@/lib/format";

const CATS = ["Semua", "Kopdar", "Touring", "Baksos", "Workshop"];

export default function EventsPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cat, setCat] = useState("Semua");

  useEffect(() => {
    api.get("/events").then((r) => setItems(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => items.filter((e) => cat === "Semua" || e.category === cat), [items, cat]);

  return (
    <div className="min-h-screen bg-[#090A0F]">
      <Navbar />
      <main className="mx-auto max-w-7xl space-y-10 px-4 pb-24 pt-32 sm:px-6 lg:px-12">
        <SectionHeading label="Agenda Chapter" title="Event & Kopdar"
          desc="Jadwal resmi kegiatan 3C Chapter Jatim: kopdar bulanan, touring, bakti sosial, dan workshop teknik." />
        <div className="flex flex-wrap gap-2">
          {CATS.map((c) => (
            <button key={c} data-testid={`event-filter-${c.toLowerCase()}`} onClick={() => setCat(c)}
              className={`rounded-md border px-3.5 py-1.5 font-mono text-xs uppercase tracking-widest transition-colors ${
                cat === c ? "border-amber-500/50 bg-amber-500/15 text-amber-400" : "border-white/10 text-slate-400 hover:border-white/25 hover:text-slate-200"
              }`}>
              {c}
            </button>
          ))}
        </div>
        {loading && <p className="text-slate-500">Memuat agenda...</p>}
        {!loading && filtered.length === 0 && (
          <p data-testid="event-empty-state" className="rounded-xl border border-white/10 bg-[#12141C]/90 p-10 text-center text-slate-400">
            Belum ada agenda pada kategori ini.
          </p>
        )}
        <div className="grid gap-6 lg:grid-cols-2">
          {filtered.map((e, i) => (
            <motion.div key={e.id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.07 }}
              data-testid={`event-card-${e.id}`}
              className="group overflow-hidden rounded-xl border border-white/10 bg-[#12141C]/90 transition-all duration-300 hover:-translate-y-1 hover:border-amber-500/40">
              {e.image_url && (
                <div className="relative h-48 overflow-hidden">
                  <img src={e.image_url} alt={e.title} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                  <div className="absolute left-4 top-4 rounded-lg border border-amber-500/40 bg-[#090A0F]/90 px-3 py-2 text-center backdrop-blur">
                    <p className="font-heading text-2xl font-bold leading-none text-amber-400">{dayOf(e.date)}</p>
                    <p className="font-mono text-[10px] uppercase tracking-widest text-amber-400/80">{monthShortID(e.date)}</p>
                  </div>
                  <span className="absolute right-4 top-4 rounded-md border border-white/15 bg-[#090A0F]/80 px-2.5 py-1 font-mono text-xs uppercase tracking-widest text-slate-200 backdrop-blur">
                    {e.category}
                  </span>
                </div>
              )}
              <div className="space-y-3 p-6">
                <h3 className="font-heading text-2xl font-bold uppercase leading-tight text-white group-hover:text-amber-400 sm:text-3xl">{e.title}</h3>
                <p className="text-sm leading-relaxed text-slate-400">{e.description}</p>
                <div className="space-y-1.5 border-t border-white/10 pt-3 text-sm text-slate-300">
                  <p className="flex items-center gap-2"><CalendarDays className="h-4 w-4 text-rose-500" />{formatDateID(e.date)}</p>
                  <p className="flex items-center gap-2"><Clock className="h-4 w-4 text-rose-500" />{e.time}</p>
                  <p className="flex items-center gap-2"><MapPin className="h-4 w-4 text-rose-500" />{e.location}</p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </main>
      <Footer />
    </div>
  );
}
